import ComponentHeader from '@atoms/ComponentHeader/ComponentHeader';
import { ICameraImage } from '@models/ICameraImage';
import { ILink } from '@models/ILink';
import ImageLightBoxTypeEnum from '@models/enums/ImageLightBoxTypeEnum';
import Carousel from '@molecules/Carousel/Carousel';
import ImageLightBox from '@molecules/ImageLightBox/ImageLightBox';
import { useState } from 'react';
import { SwiperSlide } from 'swiper/react';

interface Props {
  readonly title?: string;
  readonly subTitle?: string;
  readonly link?: ILink;
  readonly images: ICameraImage[];
  readonly imageType: ImageLightBoxTypeEnum;
  readonly showYellowTick: boolean;
  readonly anchorId?: string;
}

const ImageCarousel = ({
  title,
  subTitle,
  link,
  images,
  imageType,
  showYellowTick,
  anchorId = '',
}: Props) => {
  const [isSwiperBeginning, setIsSwiperBeginning] = useState(true);
  const [isSwiperEnd, setIsSwiperEnd] = useState(false);

  const imageClass = 'h-[220px] lg:h-[400px] xl:h-[640px] w-auto';
  const navButtonId = `image-carousel-${anchorId}`;

  return (
    <section
      id={`a-${anchorId}`}
      className="noselect component-padding flex min-h-[220px] flex-col items-center justify-center overflow-hidden bg-white text-black-100 lg:min-h-[400px] lg:gap-10 xl:min-h-[640px] dark:bg-black-100 dark:text-white"
      data-testid="CT-44-Image-Carousel-Block"
    >
      {(title || subTitle || link?.url) && (
        <ComponentHeader
          title={title}
          subtitle={subTitle}
          subtitleClass="text-grey-450 dark:text-grey-500 body-two"
          showYellowTick={showYellowTick}
          cta={link}
          dataTestId="CT-44-Image-Carousel-Component-Header"
          navButtonId={navButtonId}
          isLeftNavButtonDisabled={isSwiperBeginning}
          isRightNavButtonDisabled={isSwiperEnd}
        />
      )}

      {images?.length > 0 && (
        <div className="image-carousel">
          <div className="overflow-swiper container overflow-visible">
            <Carousel
              className="w-full"
              breakpoints={{
                768: {
                  spaceBetween: 30,
                },
              }}
              observer
              observeParents
              onInit={(swiper) => {
                swiper.update();
              }}
              onSetIsSwiperBeginning={setIsSwiperBeginning}
              onSetIsSwiperEnd={setIsSwiperEnd}
              slidesPerView="auto"
              spaceBetween={20}
              navId={navButtonId}
            >
              {images.map((image, index) => (
                <SwiperSlide
                  key={`img-carousel-slide-${image.meta?.contentLink}`}
                  className="relative"
                >
                  <ImageLightBox
                    contentIds={images.map(
                      ({ meta: imageMeta }) => imageMeta?.contentLink as string
                    )}
                    imageClasses={imageClass}
                    imageType={imageType}
                    {...image}
                    dataTestId={`CT-44-Image-Carousel-Lightbox-${index}`}
                  />
                </SwiperSlide>
              ))}
            </Carousel>
          </div>
        </div>
      )}
    </section>
  );
};

export default ImageCarousel;
